import { useEffect, useState, useCallback } from 'react'
import { api } from '../../lib/api'
import { Button } from '../ui/Button'
import type { DeviceCredential, CredentialType } from '@shared/types'

const PlusIcon = <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>

interface Props {
  siteId: string
  deviceId: string  // slug
  deviceLabel: string
}

interface CredentialDisplay extends DeviceCredential {
  _revealed?: boolean
  _revealedPassword?: string
}

export function DeviceCredentialsPanel({ siteId, deviceId, deviceLabel: _deviceLabel }: Props) {
  const [credentials, setCredentials] = useState<CredentialDisplay[]>([])
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)

  const load = useCallback(() => {
    api.get<DeviceCredential[]>(`/sites/${siteId}/devices/${deviceId}/credentials`)
      .then(c => setCredentials(c))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [siteId, deviceId])

  useEffect(() => { load() }, [load])

  const revealPassword = async (cred: CredentialDisplay) => {
    if (cred._revealed) {
      setCredentials(prev => prev.map(c =>
        c.id === cred.id ? { ...c, _revealed: false } : c
      ))
      return
    }

    try {
      const result = await api.get<{ username: string; password: string }>(`/device-credentials/${cred.id}/reveal`)
      setCredentials(prev => prev.map(c =>
        c.id === cred.id ? { ...c, _revealed: true, _revealedPassword: result.password } : c
      ))
    } catch { /* ignore */ }
  }

  const copyPassword = async (cred: CredentialDisplay) => {
    try {
      let password: string
      if (cred._revealedPassword) {
        password = cred._revealedPassword
      } else {
        const result = await api.get<{ username: string; password: string }>(`/device-credentials/${cred.id}/reveal`)
        password = result.password
      }
      await navigator.clipboard.writeText(password)
    } catch { /* ignore */ }
  }

  const deleteCred = async (id: string) => {
    if (!confirm('Delete this credential?')) return
    try {
      await api.delete(`/device-credentials/${id}`)
      setCredentials(prev => prev.filter(c => c.id !== id))
    } catch { /* ignore */ }
  }

  if (loading) return <div style={{ fontSize: 12, color: 'var(--text-tertiary)', padding: 8 }}>Loading credentials...</div>

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{
        fontSize: 12, fontWeight: 700, color: 'var(--text-tertiary)',
        textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8,
      }}>Credentials</div>

      {credentials.map(cred => (
        <div key={cred.id} style={{
          background: 'var(--bg-inset)', borderRadius: 'var(--radius)',
          padding: 12, marginBottom: 8,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-secondary)' }}>
              {cred.credential_type} Login
            </span>
            <button
              onClick={() => deleteCred(cred.id)}
              style={{ background: 'none', border: 'none', fontSize: 14, cursor: 'pointer', color: 'var(--text-tertiary)', padding: 0 }}
              title="Delete credential"
            >x</button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: '4px 8px', fontSize: 12 }}>
            <span style={{ color: 'var(--text-tertiary)' }}>Username</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 500 }}>{cred.username}</span>

            <span style={{ color: 'var(--text-tertiary)' }}>Password</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 500 }}>
                {cred._revealed ? cred._revealedPassword : '••••••••'}
              </span>
              <button
                onClick={() => revealPassword(cred)}
                style={iconBtnStyle}
                title={cred._revealed ? 'Hide password' : 'Reveal password'}
              >
                {cred._revealed ? '◉' : '○'}
              </button>
              <button
                onClick={() => copyPassword(cred)}
                style={iconBtnStyle}
                title="Copy to clipboard"
              >
                ⧉
              </button>
            </div>

            {cred.port && <>
              <span style={{ color: 'var(--text-tertiary)' }}>Port</span>
              <span style={{ fontFamily: 'var(--font-mono)' }}>{cred.port}</span>
            </>}
            {cred.protocol && <>
              <span style={{ color: 'var(--text-tertiary)' }}>Protocol</span>
              <span style={{ textTransform: 'uppercase' }}>{cred.protocol}</span>
            </>}
          </div>

          {cred.last_verified && (
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 6 }}>
              Last verified: {new Date(cred.last_verified).toLocaleDateString()}
            </div>
          )}
        </div>
      ))}

      {showAdd ? (
        <AddCredentialForm
          siteId={siteId}
          deviceId={deviceId}
          onSaved={() => { setShowAdd(false); load() }}
          onCancel={() => setShowAdd(false)}
        />
      ) : (
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            onClick={() => setShowAdd(true)}
            variant="secondary"
            size="sm"
            icon={PlusIcon}
          >
            Add Credential
          </Button>
        </div>
      )}
    </div>
  )
}

function AddCredentialForm({
  siteId, deviceId, onSaved, onCancel,
}: {
  siteId: string; deviceId: string; onSaved: () => void; onCancel: () => void
}) {
  const [form, setForm] = useState({
    credential_type: 'admin' as CredentialType,
    username: '',
    password: '',
    port: '',
    protocol: 'http',
  })
  const [saving, setSaving] = useState(false)

  const save = async () => {
    if (!form.username || !form.password) return
    setSaving(true)
    try {
      await api.post(`/sites/${siteId}/devices/${deviceId}/credentials`, {
        credential_type: form.credential_type,
        username: form.username,
        password: form.password,
        port: form.port ? parseInt(form.port) : undefined,
        protocol: form.protocol || undefined,
      })
      onSaved()
    } catch { /* ignore */ }
    setSaving(false)
  }

  return (
    <div style={{
      background: 'var(--bg-inset)', borderRadius: 'var(--radius)',
      padding: 12, display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <select
        value={form.credential_type}
        onChange={e => setForm(f => ({ ...f, credential_type: e.target.value as CredentialType }))}
        style={inputStyle}
      >
        <option value="admin">Admin</option>
        <option value="onvif">ONVIF</option>
        <option value="ssh">SSH</option>
        <option value="rdp">RDP</option>
        <option value="web">Web</option>
        <option value="api">API</option>
      </select>
      <input
        placeholder="Username" value={form.username}
        onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
        style={inputStyle}
      />
      <input
        type="password" placeholder="Password" value={form.password}
        onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
        style={inputStyle}
      />
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          placeholder="Port" value={form.port}
          onChange={e => setForm(f => ({ ...f, port: e.target.value }))}
          style={{ ...inputStyle, width: 80 }}
        />
        <select
          value={form.protocol}
          onChange={e => setForm(f => ({ ...f, protocol: e.target.value }))}
          style={inputStyle}
        >
          <option value="http">HTTP</option>
          <option value="https">HTTPS</option>
          <option value="ssh">SSH</option>
          <option value="rdp">RDP</option>
          <option value="rtsp">RTSP</option>
        </select>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <Button onClick={save} loading={saving} size="sm">
          Save
        </Button>
        <Button onClick={onCancel} variant="secondary" size="sm">Cancel</Button>
      </div>
    </div>
  )
}

const iconBtnStyle: React.CSSProperties = {
  background: 'none', border: 'none', fontSize: 14,
  cursor: 'pointer', color: 'var(--text-tertiary)',
  padding: '0 2px', lineHeight: 1,
}

const inputStyle: React.CSSProperties = {
  padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)',
  background: 'var(--bg-surface)', fontSize: 12, width: '100%',
  outline: 'none',
}
